/**
 * Checkout event subscribers — side effects that fire after order.placed.
 * Each subscriber is independent: one failing doesn't affect the others.
 * Add new side effects here without touching the checkout workflow.
 *
 * Notifications use the provider registry (Uptime Kuma pattern) —
 * add SMS, Telegram, Slack by registering a provider, not editing this file.
 */

import { supabase } from "./supabase";
import { retryRpc } from "./supabase-retry";
import { on, EVENTS } from "./event-bus";
import { lineDisplayName } from "./utils/packingSlip";

// Order emails name each line the way the packing slip does ("GLP-3 20mg",
// "BAC Water 10mL"). The cart holds bare product names; the size lives in the
// product row's option_label/subtitle, which every cart item carries.
// Alex 2026-10-01: "This email doesn't show mg for glp 3 or ml for BAC water".
export const emailItems = cart => cart.map(i => ({
  id: i.id,
  name: lineDisplayName({ ...i, product_id: i.id }, { [i.id]: i }),
  qty: i.qty,
  price: i.price,
}));
import { notify, registerDefaultProviders } from "./notification-providers";

// --- Coupon usage counter ---
function incrementCouponUsage({ appliedCoupon }) {
  if (!appliedCoupon?.id) return;
  return retryRpc(supabase, "increment_coupon_usage", { coupon_id: appliedCoupon.id })
    .then(({ error }) => { if (error) throw new Error(error.message); });
}
incrementCouponUsage._name = "increment_coupon_usage";

// --- Affiliate commission ---
function recordAffiliateCommission({ orderId, refCode, totals }) {
  if (!refCode) return;
  return retryRpc(supabase, "record_affiliate_commission", {
    p_order_id: orderId,
    p_ref_code: refCode,
    p_order_total: totals.subtotal,
  }).then(({ data, error }) => {
    if (!error && data?.ok) localStorage.removeItem("_ref");
    else throw new Error(error?.message || data?.error || "Affiliate commission failed");
  });
}
recordAffiliateCommission._name = "record_affiliate_commission";

// --- Customer notifications (via provider registry) ---
function sendCustomerNotifications({ user, orderId, cart, totals, resolvedAddress, paymentMethod, paymentHandles, appliedCoupon, bulkDiscount }) {
  const { subtotal, discount, shipping, total } = totals;
  return notify("customer", {
    to: user.email,
    customerName: resolvedAddress.name || user.email,
    orderId,
    items: emailItems(cart),
    subtotal,
    bulkDiscount: Number(bulkDiscount) || 0,
    discount,
    shipping,
    total,
    shippingAddress: resolvedAddress,
    paymentMethod,
    paymentHandle: paymentHandles[`${paymentMethod}_handle`] ?? "",
    couponCode: appliedCoupon?.code ?? null,
  }).then(results => {
    const failed = results.filter(r => !r.ok);
    if (failed.length > 0) throw new Error(`Customer notification failed: ${failed.map(f => f.provider).join(", ")}`);
  });
}
sendCustomerNotifications._name = "send_customer_notifications";

// --- Admin notifications (via provider registry) ---
function sendAdminNotifications({ orderId, user, cart, totals, resolvedAddress, paymentMethod }) {
  return notify("admin", {
    orderId,
    customerName: resolvedAddress.name || user.email,
    items: emailItems(cart),
    total: totals.total,
    paymentMethod,
  }).then(results => {
    const failed = results.filter(r => !r.ok);
    if (failed.length > 0) throw new Error(`Admin notification failed: ${failed.map(f => f.provider).join(", ")}`);
  });
}
sendAdminNotifications._name = "send_admin_notifications";

// --- Register all subscribers ---
export function registerCheckoutSubscribers() {
  registerDefaultProviders();
  on(EVENTS.ORDER_PLACED, incrementCouponUsage);
  on(EVENTS.ORDER_PLACED, recordAffiliateCommission);
  on(EVENTS.ORDER_PLACED, sendCustomerNotifications);
  on(EVENTS.ORDER_PLACED, sendAdminNotifications);
}
